import { useContext } from 'react'

import {
  Box, Button, Columns, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useLayoutTemplate from '@hooks/useLayoutTemplate'
import useAssignEntityPermissionsModal from '@hooks/useAssignEntityPermissionsModal'

import Publications from './components/Publications'
import PublicationProducts from './components/PublicationProducts'
import StepNavigation from './components/StepNavigation'

const MODAL_KEY = 'AssignLayoutTemplatePermissionsModal'

type ModalCallbacks = {
  bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
  createEntityName: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const AssignLayoutTemplatePermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const { closeModal, updateCompatibleProducts } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})

  const initStepKey = digObject(modalPayload, 'stepKey')
  const initLayoutTemplate = digObject(modalPayload, 'layoutTemplate.id')

  const entityPayload = useLayoutTemplate({ id: initLayoutTemplate })
  const {
    callbacks: { updateCompatibleProducts: updateProductsFn },
    layoutTemplate,
  } = entityPayload

  const assignLayoutTemplatePermissionsPayload = useAssignEntityPermissionsModal({
    activeStepKey: initStepKey,
    customStepKeys: {
      publicationIds: 'publicationProductIds',
      publicationProductIds: '',
    },
    entity: layoutTemplate,
  })

  const {
    activeStepKey,
    callbacks: { preparePermissions, setCurrentStepKey },
    hasNextStep,
    nextStepKey,
    permissions: {
      queued: queuedPermissions,
    },
    saveEnabled,
  } = assignLayoutTemplatePermissionsPayload

  // To be used with handleCallbackAction
  const updateCompatibleProductsPayload = {
    callbacks: {
      action: updateProductsFn,
      afterAction: closeModal,
      buildActionParams: () => [preparePermissions()],
    },
    toastText: 'Products Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} showModal={showModal} size="fullscreen">
      <SidebarModal.Header callbacks={callbacks} title="Assign" titleSecondLine="Permissions" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>

            {activeStepKey === 'publicationIds' && (
              <Publications assignLayoutTemplatePermissionsPayload={assignLayoutTemplatePermissionsPayload} />
            )}

            {activeStepKey === 'publicationProductIds' && (
              <PublicationProducts assignLayoutTemplatePermissionsPayload={assignLayoutTemplatePermissionsPayload} />
            )}

          </Columns.Main>

          <Columns.Sidebar>
            <StepNavigation assignLayoutTemplatePermissionsPayload={assignLayoutTemplatePermissionsPayload} />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Box marginLeft="auto" width="auto">
          {hasNextStep && (
            <Button
              buttonStyle="primaryEdit"
              disabled={!queuedPermissions.publicationIds.length}
              loadingBubbleColor="white"
              onClick={nextStepKey ? () => setCurrentStepKey(nextStepKey) : null}
              size="large"
              width="auto"
            >
              Next Step
            </Button>
          )}

          {(saveEnabled || !hasNextStep) && activeStepKey !== 'publicationIds' && (
            <Button
              buttonStyle="primaryCreate"
              loadingBubbleColor="white"
              onClick={() => updateCompatibleProducts(updateCompatibleProductsPayload)}
              marginLeft="medium"
              size="large"
              width="auto"
            >
              Save Permissions
            </Button>
          )}
        </Box>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <AssignLayoutTemplatePermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
